import { SupabaseClient } from '@supabase/supabase-js';
import camelcaseKeys from 'camelcase-keys';
import { z } from 'zod';

import { Database, withLogs } from '@core';

import { apiClient, ApiConfig } from '../apiClient';

const ActivitiesSchema = z.object({
    id: z.number(),
    clubs: z
        .object({
            shortName: z.string(),
        })
        .nullable()
        .optional(),
    leftHanded: z.boolean(),
    activityTagLink: z
        .array(
            z.object({
                activityTags: z
                    .object({
                        name: z.string(),
                    })
                    .nullable(),
            }),
        )
        .transform((links) => links.map((l) => l.activityTags?.name).filter((v): v is string => typeof v === 'string'))
        .default([]),
});

const SwingDbSchema = z.object({
    id: z.number(),
    uuid: z.string(),
    activityId: z.number(),
    createdAt: z.string(),
    favorite: z.boolean(),
    name: z.string().nullable(),
    activities: ActivitiesSchema,
});

const SwingResponseSchema = z.object({
    uuid: z.string(),
    activityId: z.number(),
    createdAt: z.string(),
    favorite: z.boolean(),
    name: z.string().nullable(),
});

export type SwingResponse = z.infer<typeof SwingResponseSchema>;

export type SwingAnalysisMetadata = { id: number; typeId: number };

export type Swing = {
    uuid: string;
    activityId: number;
    createdAt: string;
    favorite: boolean;
    name: string | null;
    clubName?: string;
    leftHanded?: boolean;
    tags?: string[];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mapSwing = (input: any): Swing => {
    const swing = camelcaseKeys(input, { deep: true });
    const result = SwingDbSchema.safeParse(swing);
    if (result.error) {
        throw result.error;
    }
    const parsed = result.data;

    return {
        uuid: parsed?.uuid,
        activityId: parsed.activities.id,
        createdAt: parsed.createdAt,
        favorite: parsed.favorite,
        name: parsed.name,
        leftHanded: parsed.activities.leftHanded,
        clubName: parsed.activities?.clubs?.shortName,
        tags: parsed.activities.activityTagLink,
    };
};

// We do !inner join so that we do .eq checks with activities.user_id, etc.
export const SWING_QUERY = `
            id,
            uuid,
            activity_id,
            created_at,
            favorite,
            name,
            goal_id,
            activities!inner(
                *, 
                clubs(short_name), 
                activity_tag_link(
                    activity_tags(*)
                )
            )
            ` as const;

export const fetchSwingByUUID = withLogs(async (uuid: string): Promise<SwingResponse> => {
    const { data } = await apiClient.get<SwingResponse>(ApiConfig.path(`/api/v1/swings/${uuid}`));
    return SwingResponseSchema.parse(data);
}, 'fetchSwingByUUID');

export const deleteSwing = withLogs(async (uuid: string): Promise<void> => {
    await apiClient.delete(ApiConfig.path(`/api/v1/swings/${uuid}`));
}, 'deleteSwing');

export const fetchSwingById = withLogs(
    async (supabase: SupabaseClient<Database>, id: number): Promise<Swing | null> => {
        const { data, error } = await supabase
            .from('swings')
            .select(SWING_QUERY)
            .eq('id', id)
            .eq('deleted', false)
            .limit(1)
            .single();

        if (error) {
            throw error;
        }

        return mapSwing(data);
    },
    'fetchSwingById',
);

/**
 * Update swing name and favorite status
 */
export const updateSwingNameAndFavoriteStatus = withLogs(
    async ({ name, uuid }: Pick<Swing, 'name' | 'uuid'>): Promise<Pick<Swing, 'name' | 'uuid' | 'favorite'>> => {
        const { data } = await apiClient.patch<Swing>(ApiConfig.path(`/api/v1/swings/${uuid}`), {
            favorite: !!name,
            name,
        });

        return data;
    },
    'updateSwingNameAndFavoriteStatus',
);
